import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useFormatters, useI18n } from '../i18n';
import { ApiError, getPublicMap } from '../lib/api';
import { parseInstant } from '../lib/format';
import type { PublicMap } from '../lib/types';
import { ErrorNotice } from '../components/ErrorNotice';
import { MapWorkspace } from '../features/result/MapWorkspace';
import { analysisMessageKey } from '../features/analysis/parameters';

/**
 * A shared map, viewable with no sign-in.
 *
 * Nothing on this route touches Firebase Auth, so an unauthenticated visitor
 * never sees a sign-in wall and the Firebase SDK is never initialised for them.
 */
export function PublicMapRoute() {
  const { shareId = '' } = useParams<{ shareId: string }>();
  const { t } = useI18n();
  const fmt = useFormatters();

  const [map, setMap] = useState<PublicMap | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    setMap(null);
    getPublicMap(shareId, controller.signal)
      .then(setMap)
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.code === 'cancelled') return;
        setError(cause);
      });
    return () => controller.abort();
  }, [shareId, nonce]);

  if (error) {
    const missing = error instanceof ApiError && (error.code === 'not_found' || error.status === 404);
    return (
      <div className="centre-state">
        {missing ? (
          <>
            <p>{t('public.notFound')}</p>
            <Link className="button" to="/">
              {t('public.cta')}
            </Link>
          </>
        ) : (
          <div style={{ maxWidth: '32rem', width: '100%' }}>
            <ErrorNotice
              error={error}
              title={t('error.loadTitle')}
              onRetry={() => setNonce((n) => n + 1)}
            />
          </div>
        )}
      </div>
    );
  }

  if (!map) {
    return (
      <div className="centre-state">
        <p>{t('result.loading')}</p>
      </div>
    );
  }

  const created = parseInstant(map.created_at);

  return (
    <MapWorkspace
      name={map.name}
      vos={map.map}
      build={map.build}
      warnings={map.warnings}
      nDocuments={map.n_documents}
      exportTarget={{ kind: 'public', shareId: map.share_id }}
      banner={
        <div className="public-bar">
          <span className="badge">{t('public.badge')}</span>
          <span>
            {t('public.meta', {
              analysis: t(analysisMessageKey(map.analysis)),
              items: fmt.int(map.build.n_items),
              date: created ? fmt.date(created) : t('common.dash'),
            })}
          </span>
          <span className="header__spacer" />
          <Link to="/">{t('public.cta')}</Link>
        </div>
      }
    />
  );
}
